import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Printer, Split, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { API_BASE, idem } from '@/utils/api';
import { useTableStates } from '@/components/tables/useTableStates';
import { STATE_META, urgencyRank, money } from '@/components/tables/tableStates';
import ComensalesSplitDialog from '@/components/ComensalesSplitDialog';
import { PaymentMethodGrid, TipPicker } from '../ModernCheckout';
import { fmtElapsed } from '../shared';

const mins = (iso, now) => Math.max(0, Math.floor((now - new Date(iso)) / 60000));

export const CashTabs = ({ active }) => (
  <div className="flex gap-5 border-b border-border px-1" role="tablist">
    {[['/cashier', 'Cola de cobro'], ['/cash-register', 'Apertura y cierre']].map(([to, l]) => (
      <Link key={to} to={to} role="tab" aria-selected={active === to} className="m-tab pb-2 text-[15px] font-medium text-muted-foreground" data-testid={`cash-tab-${to.slice(1)}`}>{l}</Link>
    ))}
  </div>
);

const Bill = ({ details, unit }) => {
  const lines = (details?.orders || []).filter(o => o.status !== 'Cancelado').flatMap(o => o.items || []);
  return (
    <div className="m-card p-4 space-y-3" data-testid="cash-bill">
      <div><h2 className="text-lg font-bold">Cuenta – {unit.name}</h2><p className="text-sm text-muted-foreground">{unit.guest_count || 1} personas · Mozo: {unit.waiter_name || 'sin asignar'}</p></div>
      <table className="w-full text-sm">
        <thead><tr className="text-left text-muted-foreground border-b border-border">{['Cant.', 'Producto', 'Precio', 'Total'].map(h => <th key={h} className="py-1.5 font-semibold">{h}</th>)}</tr></thead>
        <tbody>{lines.map((l, i) => (
          <tr key={i} className="border-b border-border"><td className="py-1.5">{l.quantity}</td><td>{l.product_name}</td><td className="tabular-nums">{money(l.unit_price)}</td><td className="tabular-nums font-semibold">{money(l.subtotal)}</td></tr>
        ))}</tbody>
      </table>
      <dl className="text-sm space-y-1">
        <div className="flex justify-between"><dt>Subtotal</dt><dd className="tabular-nums">{money(details?.subtotal)}</dd></div>
        {details?.service_charge > 0 && <div className="flex justify-between"><dt>Servicio</dt><dd className="tabular-nums">{money(details.service_charge)}</dd></div>}
        {details?.tax > 0 && <div className="flex justify-between"><dt>Impuestos</dt><dd className="tabular-nums">{money(details.tax)}</dd></div>}
        <div className="flex justify-between text-base font-bold pt-1 border-t border-border"><dt>Total</dt><dd className="tabular-nums" data-testid="cash-bill-total">{money(details?.total)}</dd></div>
      </dl>
    </div>
  );
};

export default function ModernCashier() {
  const { units, now, refresh } = useTableStates();
  const queue = useMemo(() => units.filter(u => u.session_id && u.total > 0)
    .sort((a, b) => urgencyRank(a.state) - urgencyRank(b.state) || new Date(a.opened_at) - new Date(b.opened_at)), [units]);
  const [params] = useSearchParams();
  const [sel, setSel] = useState(params.get('unit'));
  const [details, setDetails] = useState(null);
  const [method, setMethod] = useState('cash');
  const [tip, setTip] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [discountType, setDiscountType] = useState('fixed');
  const [reason, setReason] = useState('');
  const [split, setSplit] = useState(false);
  const [busy, setBusy] = useState(false);
  const unit = queue.find(u => u.unit_id === sel) || queue[0];
  useEffect(() => { setDiscount(0); setDiscountType('fixed'); setReason(''); setTip(0); }, [unit?.unit_id]);
  useEffect(() => {
    if (!unit?.session_id) return setDetails(null);
    axios.get(`${API_BASE}/sessions/${unit.session_id}`).then(r => setDetails(r.data)).catch(() => setDetails(null));
  }, [unit?.session_id, unit?.total]); // eslint-disable-line react-hooks/exhaustive-deps
  const base = details?.total || 0;
  const discountAmount = Math.min(base, discountType === 'percent' ? base * Math.min(100, Number(discount) || 0) / 100 : Number(discount) || 0);
  const total = Math.max(0, base - discountAmount);
  const charge = async () => {
    setBusy(true);
    try {
      await axios.post(`${API_BASE}/sessions/${unit.session_id}/close`, { tip, payment_method: method, manual_discount: Math.round(discountAmount * 100) / 100,
        discount_reason: discountAmount ? (reason.trim() || (discountType === 'percent' ? `Descuento ${discount}%` : 'Descuento en caja')) : '' }, idem());
      toast.success(`${unit.name} cobrada`);
      setTip(0); setDiscount(0); setReason(''); setSel(null); refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo cobrar'); } finally { setBusy(false); }
  };
  const print = async () => {
    try { await axios.post(`${API_BASE}/print/ticket/${unit.session_id}`); toast.success('Pre-cuenta enviada a la impresora'); } catch { toast.error('No se pudo imprimir'); }
  };
  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="modern-cashier">
      <CashTabs active="/cashier" />
      <div className="grid lg:grid-cols-[280px_1fr_340px] gap-4 items-start">
        <section className="m-card p-3" data-testid="cash-queue">
          <h2 className="font-bold px-1 pb-2">Cola de cobro ({queue.length})</h2>
          <ol className="space-y-1.5">
            {queue.map((u, i) => (
              <li key={u.unit_id}>
                <button onClick={() => setSel(u.unit_id)} data-testid={`cash-queue-${u.unit_id}`}
                  className={`w-full flex items-center gap-3 rounded-lg p-2.5 text-left border ${unit?.unit_id === u.unit_id ? 'border-[#2563EB] bg-[#2563EB]/10' : 'border-transparent hover:bg-muted'}`}>
                  <span className="w-6 text-lg font-bold">{i + 1}</span>
                  <span className="flex-1 min-w-0"><span className="block font-semibold text-sm">{u.name}</span>
                    <span className="block text-xs inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: STATE_META[u.state]?.color }} />{STATE_META[u.state]?.label} · {u.waiter_name || 'Sin mozo'}</span></span>
                  <span className="text-right"><span className="block font-bold text-sm tabular-nums">{money(u.total)}</span><span className="block text-xs">{mins(u.opened_at, now)} min</span></span>
                </button>
              </li>
            ))}
            {!queue.length && <li className="text-sm text-muted-foreground p-2">No hay mesas para cobrar</li>}
          </ol>
        </section>
        {unit ? <Bill details={details} unit={unit} /> : <div className="m-card p-8 text-center text-muted-foreground">Elegí una mesa</div>}
        {unit && (
          <section className="m-card p-4 space-y-4" data-testid="cash-pay">
            <h2 className="font-bold">Medio de pago</h2>
            <PaymentMethodGrid value={method} onChange={setMethod} />
            <div><p className="text-sm font-semibold mb-2">Propina sugerida (para el mozo)</p><TipPicker base={total} tip={tip} setTip={setTip} /></div>
            <div className="space-y-2" data-testid="cash-discount">
              <div className="flex items-center justify-between gap-2 text-sm">
                <span>Descuento</span>
                <div className="flex items-center gap-2">
                  <div className="inline-flex rounded-lg bg-muted p-0.5" role="group" aria-label="Tipo de descuento">
                    {[['fixed', '$'], ['percent', '%']].map(([k, l]) => (
                      <button key={k} type="button" aria-pressed={discountType === k} onClick={() => setDiscountType(k)} data-testid={`cash-discount-type-${k}`}
                        className={`h-8 w-9 rounded-md text-sm font-semibold ${discountType === k ? 'bg-card shadow-sm' : 'text-muted-foreground'}`}>{l}</button>
                    ))}
                  </div>
                  <Input type="number" min={0} max={discountType === 'percent' ? 100 : undefined} className="w-24" value={discount}
                    onChange={e => setDiscount(e.target.value)} aria-label="Valor del descuento" data-testid="cash-discount-input" />
                </div>
              </div>
              {discountAmount > 0 && <>
                <p className="text-xs text-muted-foreground text-right" data-testid="cash-discount-amount">− {money(discountAmount)}</p>
                <Input placeholder="Motivo del descuento (opcional)" value={reason} onChange={e => setReason(e.target.value)} data-testid="cash-discount-reason" />
              </>}
            </div>
            <div className="flex items-end justify-between border-t border-border pt-3"><span className="text-sm">Monto a cobrar</span><span className="text-3xl font-bold tabular-nums" data-testid="cash-amount">{money(total + tip)}</span></div>
            <Button className="w-full h-12 text-base bg-[#2563EB] hover:bg-[#1d4ed8] text-white" disabled={busy || method === 'transfer'} onClick={charge} data-testid="cash-charge-btn">Cobrar {money(total + tip)}</Button>
            <div className="grid grid-cols-3 gap-2">
              <Button variant="outline" size="sm" onClick={() => setSplit(true)} data-testid="cash-split-btn"><Split className="w-3.5 h-3.5 mr-1" />Dividir</Button>
              <Button variant="outline" size="sm" onClick={print} data-testid="cash-print-btn"><Printer className="w-3.5 h-3.5 mr-1" />Pre-cuenta</Button>
              <Button variant="outline" size="sm" asChild data-testid="cash-whatsapp-btn"><a href={`https://wa.me/?text=${encodeURIComponent(`Cuenta ${unit.name}: ${money(total)}`)}`} target="_blank" rel="noreferrer"><Send className="w-3.5 h-3.5 mr-1" />WhatsApp</a></Button>
            </div>
            <p className="text-xs text-muted-foreground">Abierta hace {fmtElapsed(unit.opened_at, now)} h</p>
          </section>
        )}
      </div>
      {unit && <ComensalesSplitDialog open={split} onOpenChange={setSplit} session={{ session_id: unit.session_id, unit_name: unit.name, total: details?.total }} onUpdate={refresh} />}
    </div>
  );
}
